import styled from 'styled-components';
import { pxtorem } from '~/utils/tools';
import { intentToColor } from '~/utils/colors';
import { IntentSlug } from '~/types/types';

import { Wrapper } from '~/styles/Wrapper.styled';
import categoryShape from '~/styles/img/shape-8.svg';

import { StyledTestimonials } from '~/components/Product/Testimonials/Testimonials.styled';
import { StyledAccordionItem } from '~/components/Accordion/Accordion.styled';
import { StyledStarRating } from '~/components/Product/StarRating/StarRating.styled';

export const StyledProductPage = styled.div<StyledProductPageProps>`
  .thumbnails {
    position: absolute;
    display: none;
    flex-direction: column;
    left: calc(-1336px / 2 - 60px + 50vw);
    top: calc(var(--gutter) - 2px);
    gap: 8px;

    figure {
      &:hover img {
        transform: scale(1.05);
      }
      cursor: pointer;
      margin: 0;
      border-radius: 3px;
      overflow: hidden;
    }
    img {
      transition-property: transform;
      transition-duration: 0.3s;
      transition-timing-function: var(--cubic-ease);
      width: 70px;
      height: 70px;
    }
    @media (min-width: 1464px) {
      display: flex;
    }
  }

  .product {
    position: relative;
    & ${Wrapper} {
      position: relative;

      @media (min-width: 768px) {
        position: relative;
        display: flex;
        padding-top: 19px;
        justify-content: space-between;
        gap: 20px;
        padding-bottom: 150px;

        & .gallery,
        & .details {
          flex: 1;
        }

        & .details {
          position: sticky;
          top: 75px;
          align-self: flex-start;
        }
      }

      @media (min-width: 1000px) {
        gap: 40px;
      }

      @media (min-width: 1200px) {
        gap: 90px;
      }
    }

    & .gallery {
      & figure {
        display: flex;
        justify-content: center;
        align-items: center;
        margin: 0;
        background: var(--gray);
        border-radius: 10px;
        overflow: hidden;
        aspect-ratio: 1;
        margin: 0 0 20px;

        & .gatsby-image-wrapper {
          object-fit: cover;
          width: 100%;
        }

        &:last-child {
          margin-bottom: 0;
        }

        &:not(:first-child) {
          display: none;
        }

        @media (min-width: 768px) {
          &:not(:first-child) {
            display: block;
          }
        }
      }

      .swiper-slide {
        width: 95%;
      }

      .swiper-pagination {
        &-bullet {
          background-color: var(--black);
          opacity: 0.3;

          &-active {
            opacity: 1;
          }
        }
      }
    }

    & .category {
      font-size: 12px;
      line-height: 1.2;
      margin-top: ${pxtorem(20)};


      &:before {
        --size: 1.1em;
        content: '';
        display: inline-block;
        width: var(--size);
        height: var(--size);
        mask: url(${categoryShape}) center no-repeat;
        mask-size: contain;
        background-color: ${({ intentSlug }) => intentToColor(intentSlug)};
        margin-right: 12px;
        vertical-align: sub;
      }

      @media (min-width: 768px) {
        margin-top: 0;
        font-size: ${pxtorem(20)};
      }
    }

    & .related-products {
      margin-bottom: ${pxtorem(48)};
    }

    & h2 {
      font-family: var(--secondaryFont);
      font-size: ${pxtorem(20)};
      font-style: italic;
      line-height: 1;
      margin: 8px 0 16px;

      @media (min-width: 768px) {
        font-size: ${pxtorem(40)};
        line-height: 1.5;
      }
    }

    & .description {
      font-size: 12px;
      line-height: 1.33;

      & p {
        margin: 0;

        & + p {
          margin-top: 1em;
        }
      }

      @media (min-width: 768px) {
        font-size: ${pxtorem(16)};
      }
    }

    & .price-options {
      margin: 22px 0;

      .frequency {
        display: inline-block;
        min-width: 210px;
        margin-right: 0;
        margin-left: auto;
        
        .dropdown {
          height: ${pxtorem(45)};
          
          &-value button {
            span {
              font-size: ${pxtorem(16)};
            }

            padding: ${pxtorem(10)} ${pxtorem(20)};
          }

          &-options button {
            font-size: ${pxtorem(16)};
          }

        }
      }

      .info {
        display: inline-block;
        margin-left: 20px;
      }

      @media (min-width: 768px) {
        margin: 30px 0;
        margin-bottom: 25px;
        display: flex;
        align-items: center;
        gap: 20px;

        & .info {
          margin: 0;
        }
      }
    }

    & .cart-rating {
      & .cta--primary {
        display: block;
        width: 100%;
        font-weight: 500;
        
        @media (max-width: 768px) {
          margin-bottom: ${pxtorem(24)};
        }
      }

      ${StyledStarRating} {
        margin-top: 16px;
      }
    }

    @media (min-width: 768px) {
      .cart-rating {
        display: flex;
        align-items: center;
        gap: 22px;
        border-top: 1px solid rgba(19, 19, 19, 0.2);
        padding-inline: 0;
        padding-top: 28px;
        padding-bottom: 10px;

        & .cta--primary {
          width: auto;
        }

        ${StyledStarRating} {
          margin: 0 0 0 20px;
        }
      }

      & ${StyledAccordionItem} {
        & .accordionItem {
          &__trigger,
          &__content {
            padding-left: 35px;
          }
        }
      }
    }
  }

  .details button .label {
    font-weight: 300;
  }

  ${StyledTestimonials} {
    --swiper-navigation-size: 22px;
    margin-top: 90px;
    margin-bottom: 90px;
  }

  .need-to-know {
    & ${Wrapper} {
      padding: 60px 20px;
    }

    & h3 {
      font-size: ${pxtorem(40)};
      font-weight: 300;
      line-height: 1;
      margin: 0 0 0.75em;
    }

    @media (min-width: 768px) {
      & ${Wrapper} {
        padding-top: 120px;
      }

      & h3 {
        font-size: ${pxtorem(60)};
      }
    }
  }
`;

interface StyledProductPageProps {
  intentSlug?: IntentSlug;
}
